// @ts-check
import {Component} from 'panel';
import {pluralize} from 'common/util';
import {registerMPElement} from 'common/util/register-element';
import {countSymbols} from 'common/util/string';
import {ENTER_KEY_CODE, ESCAPE_KEY_CODE} from 'common/util/dom';

import template from './index.jade';
import css from './index.styl?inline';

const DEFAULT_MAX_CHARS = 500;
const DEFAULT_MIN_ROWS = 1;

class TextEditor extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'allow-empty-save': {type: `boolean`, description: `if true allow user to save empty input`, default: false},
      value: `string`,
      placeholder: `string`,
      'max-chars': {type: `number`, default: DEFAULT_MAX_CHARS},
      'min-rows': {type: `number`, default: DEFAULT_MIN_ROWS, description: `minimum number of textarea rows`},
      inline: {type: `boolean`, description: `for use in inline editing mode, contains a border around the component`},
      'hide-footer': {
        type: `boolean`,
        description: `minimize the editor ui by hiding characters remaining, save, and cancel buttons`,
      },
    };
  }

  constructor() {
    super();
    this.elRefs = {};
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        value: ``,
        focus: false,
        numComputedRows: 0,
      },
      helpers: {
        pluralize,
        allowEmptySave: () => this.attr(`allow-empty-save`),
        hasValidInput: () => this.getSanitizedValue().length && this.helpers.charactersRemaining() >= 0,
        charactersRemaining: () => this.attr(`max-chars`) - countSymbols(this.state.value),
        getNumRows: () => {
          return Math.max(this.attr(`min-rows`), this.state.numComputedRows);
        },
        handleInput: (ev) => {
          const value = ev.target.value;
          if (value !== this.state.value) {
            this.dispatchEvent(new CustomEvent(`change`, {detail: {value}}));
          }
          this.update({value});
          this.helpers.updateNumRows();
        },
        handleFocus: () => {
          this.update({focus: true});
        },
        handleBlur: () => {
          this.update({focus: false});
        },
        handleKeyUp: (ev) => {
          if (ev.keyCode === ENTER_KEY_CODE && !ev.shiftKey) {
            this.helpers.saveChanges();
            ev.preventDefault();
          } else if (ev.keyCode === ESCAPE_KEY_CODE) {
            this.helpers.cancelChanges();
          }
        },
        cancelChanges: () => {
          this.dispatchEvent(new CustomEvent(`cancel`));
        },
        saveChanges: () => {
          const value = this.getSanitizedValue();
          this.dispatchEvent(new CustomEvent(`submit`, {detail: {value}}));
        },
        updateNumRows: () => {
          if (this.elRefs.textarea) {
            const TEXT_AREA_PAD = 8;
            const LINE_HEIGHT = 18;
            const scrollHeight = this.elRefs.textarea.scrollHeight;
            const numComputedRows = (scrollHeight - TEXT_AREA_PAD * 2) / LINE_HEIGHT;
            this.update({numComputedRows});
          }
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => this.helpers.updateNumRows());
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `value`) {
      this.update({value: newValue});
    }
  }

  getSanitizedValue() {
    return this.state.value.trim();
  }

  focus() {
    this.el.querySelector(`textarea`).focus();
  }

  setElRef(name, el) {
    this.elRefs[name] = el;
  }
}

export default registerMPElement(`mp-text-editor`, TextEditor);
